/**
 * t605u: timesheet files for payroll software. Hours are first turned into a neutral list of pay lines (one per
 * published shift or approved time entry), then a formatter writes the file a payroll product imports. Adding Xero,
 * Sage or BrightPay later is another formatter over the same lines; the pay schedule, period and source logic stays.
 *
 * Every pay line is whole minutes and self-consistent: end - start - break = paid minutes exactly. A day with more than
 * one unpaid break gets ONE combined break of their total length (starting at the first break), because payroll
 * timesheets take one break per line; the real breaks are listed in the comment.
 */
import { DateTime } from 'luxon';
import type { ISODate, ISODateTime, UUID } from '../types';
import type { ShiftBreak } from './breaks';
import { entryMinutes } from './time';

// ---- pay schedules -------------------------------------------------------------------------------------------------

export type PayFrequency = 'weekly' | 'fortnightly' | 'four_weekly' | 'monthly';
export const PAY_FREQUENCIES: { id: PayFrequency; label: string }[] = [
  { id: 'weekly', label: 'Weekly' }, { id: 'fortnightly', label: 'Fortnightly' }, { id: 'four_weekly', label: 'Four-weekly' }, { id: 'monthly', label: 'Monthly' },
];
export const payFrequencyLabel = (f: PayFrequency | null | undefined) => PAY_FREQUENCIES.find(x => x.id === f)?.label ?? 'Not set';
export const isPayFrequency = (v: unknown): v is PayFrequency => PAY_FREQUENCIES.some(f => f.id === v);

/** Workspace settings that say where each schedule's periods start. */
export interface PayScheduleSettings {
  /** ISO weekday a weekly period starts on (1 = Monday). */
  payroll_weekly_start_day?: number | null;
  /** Any date a fortnightly / four-weekly period starts on; periods repeat every 14 / 28 days from it. */
  payroll_fortnightly_anchor?: ISODate | null;
  payroll_four_weekly_anchor?: ISODate | null;
  /** Day of the month a monthly period starts on (1-28). */
  payroll_monthly_start_day?: number | null;
}
export const DEFAULT_PAY_ANCHOR = '2026-04-06';

export interface PayPeriod { start: ISODate; end: ISODate }

const day = (iso: string) => DateTime.fromISO(iso.slice(0, 10), { zone: 'utc' });
const iso = (d: DateTime) => d.toISODate()!;

/** The pay period of a schedule that contains a (local) date. Periods are calendar dates, so clock changes never move them. */
export function payPeriodContaining(freq: PayFrequency, s: PayScheduleSettings, date: ISODate): PayPeriod {
  const d = day(date);
  if (freq === 'weekly') {
    const startDay = clampInt(s.payroll_weekly_start_day, 1, 7, 1);
    const start = d.minus({ days: (d.weekday - startDay + 7) % 7 });
    return { start: iso(start), end: iso(start.plus({ days: 6 })) };
  }
  if (freq === 'monthly') {
    const sd = clampInt(s.payroll_monthly_start_day, 1, 28, 1);
    const start = d.day >= sd ? d.set({ day: sd }) : d.minus({ months: 1 }).set({ day: sd });
    return { start: iso(start), end: iso(start.plus({ months: 1 }).minus({ days: 1 })) };
  }
  const len = freq === 'fortnightly' ? 14 : 28;
  const anchorIso = (freq === 'fortnightly' ? s.payroll_fortnightly_anchor : s.payroll_four_weekly_anchor) || DEFAULT_PAY_ANCHOR;
  const anchor = day(String(anchorIso));
  const diff = Math.round(d.diff(anchor, 'days').days);
  const start = anchor.plus({ days: Math.floor(diff / len) * len });
  return { start: iso(start), end: iso(start.plus({ days: len - 1 })) };
}

export const previousPayPeriod = (freq: PayFrequency, s: PayScheduleSettings, p: PayPeriod) => payPeriodContaining(freq, s, iso(day(p.start).minus({ days: 1 })));

/** True when from/to is exactly one period of the schedule. */
export const isPayPeriod = (freq: PayFrequency, s: PayScheduleSettings, p: PayPeriod) => {
  const x = payPeriodContaining(freq, s, p.start);
  return x.start === p.start && x.end === p.end;
};

/**
 * Periods to pick from, newest first: the period in progress (if `includeCurrent`), then `count` completed ones.
 * The default choice is the first completed one (the latest period that has ended).
 */
export function payPeriodOptions(freq: PayFrequency, s: PayScheduleSettings, today: ISODate, count = 12, includeCurrent = true): (PayPeriod & { completed: boolean })[] {
  const current = payPeriodContaining(freq, s, today);
  const out: (PayPeriod & { completed: boolean })[] = includeCurrent ? [{ ...current, completed: false }] : [];
  let p = current;
  for (let i = 0; i < count; i++) { p = previousPayPeriod(freq, s, p); out.push({ ...p, completed: true }); }
  return out;
}

/** "Mon 27 Oct – Sun 2 Nov 2026" (en-GB). */
export function describePayPeriod(p: PayPeriod): string {
  const a = day(p.start), b = day(p.end);
  const left = a.toFormat(a.year === b.year ? 'ccc d MMM' : 'ccc d MMM yyyy', { locale: 'en-GB' });
  return `${left} – ${b.toFormat('ccc d MMM yyyy', { locale: 'en-GB' })}`;
}

function clampInt(v: unknown, lo: number, hi: number, dflt: number) {
  const n = Number(v);
  return Number.isInteger(n) && n >= lo && n <= hi ? n : dflt;
}

// ---- neutral pay lines ---------------------------------------------------------------------------------------------

export type PayLineSource = 'shift' | 'timesheet';

export interface PayLine {
  user_id: UUID;
  display_name: string;
  payroll_id: string | null;
  source: PayLineSource;
  /** The shift or time entry id. */
  ref_id: UUID;
  /** Local date the line starts on. */
  date: ISODate;
  /** Whole-minute instants: end - start - (break end - break start) = paid_minutes. */
  start_at: ISODateTime;
  end_at: ISODateTime;
  break_start_at: ISODateTime | null;
  break_end_at: ISODateTime | null;
  paid_minutes: number;
  unpaid_break_minutes: number;
  /** The real unpaid breaks (start null = an "Any time" break), for notes. */
  unpaid_breaks: { start_at: ISODateTime | null; minutes: number }[];
  location_id: UUID;
  location_code: string | null;
  location_name: string;
  position_id: UUID | null;
  position_name: string | null;
}

type Person = { user_id: UUID; display_name: string | null; payroll_id: string | null };
type Place = { location_id: UUID; location_code: string | null; location_name: string; position_id: UUID | null; position_name: string | null };

const MIN = 60000;
const floorMinute = (t: number) => Math.floor(t / MIN) * MIN;
const roundMinute = (t: number) => Math.round(t / MIN) * MIN;
const at = (t: number) => new Date(t).toISOString();

/** One combined unpaid break of `unpaid` minutes inside [start, end]: from the first real break, else mid-line. */
function placeBreak(start: number, end: number, unpaid: number, first: number | null): [number, number] | null {
  if (unpaid <= 0) return null;
  const gross = (end - start) / MIN;
  let bs = first != null ? roundMinute(first) : start + Math.floor((gross - unpaid) / 2) * MIN;
  bs = Math.min(Math.max(bs, start), end - unpaid * MIN);
  return [bs, bs + unpaid * MIN];
}

const cleanId = (v: string | null | undefined) => (v ?? '').trim() || null;

/** Shift hours: the scheduled times minus unpaid breaks (paid breaks stay in the hours). */
export function shiftPayLine(s: Person & Place & { id: UUID; starts_at: string; ends_at: string; local_date: ISODate; breaks?: ShiftBreak[] | null; break_minutes?: number | null }, tz: string): PayLine {
  const start = floorMinute(Date.parse(s.starts_at));
  const gross = Math.max(0, Math.round((Date.parse(s.ends_at) - start) / MIN));
  const end = start + gross * MIN;
  const unpaidList = (s.breaks ?? []).filter(b => !b.paid)
    .sort((a, b) => (typeof a.offset_minutes === 'number' ? a.offset_minutes : 1e9) - (typeof b.offset_minutes === 'number' ? b.offset_minutes : 1e9));
  let unpaid = unpaidList.length ? unpaidList.reduce((n, b) => n + Math.max(0, Math.round(b.duration_minutes)), 0) : Math.max(0, Math.round(s.break_minutes ?? 0));
  unpaid = Math.min(unpaid, gross);
  const timed = unpaidList.filter(b => typeof b.offset_minutes === 'number').map(b => start + (b.offset_minutes as number) * MIN).sort((a, b) => a - b);
  const brk = placeBreak(start, end, unpaid, timed[0] ?? null);
  return {
    user_id: s.user_id, display_name: s.display_name ?? '', payroll_id: cleanId(s.payroll_id), source: 'shift', ref_id: s.id,
    date: localDate(start, tz), start_at: at(start), end_at: at(end), break_start_at: brk ? at(brk[0]) : null, break_end_at: brk ? at(brk[1]) : null,
    paid_minutes: gross - unpaid, unpaid_break_minutes: unpaid,
    unpaid_breaks: unpaidList.length ? unpaidList.map(b => ({ start_at: typeof b.offset_minutes === 'number' ? at(start + b.offset_minutes * MIN) : null, minutes: Math.round(b.duration_minutes) }))
      : unpaid ? [{ start_at: null, minutes: unpaid }] : [],
    location_id: s.location_id, location_code: cleanId(s.location_code), location_name: s.location_name, position_id: s.position_id, position_name: s.position_name,
  };
}

/** Timesheet hours: the paid (rounded) clock times; paid minutes exactly as the timesheet and the standard CSV show them. */
export function timesheetPayLine(e: Person & Place & { id: UUID; clock_in_at: string; clock_out_at: string; paid_in_at?: string | null; paid_out_at?: string | null;
  breaks?: { started_at: string; ended_at: string | null; paid: boolean }[] | null }, tz: string): PayLine {
  const pin = Date.parse(e.paid_in_at ?? e.clock_in_at), pout = Date.parse(e.paid_out_at ?? e.clock_out_at);
  const start = roundMinute(pin);
  const gross = Math.max(0, Math.round((pout - pin) / MIN));
  const end = start + gross * MIN;
  const paid = Math.min(gross, entryMinutes({ clock_in_at: e.clock_in_at, clock_out_at: e.clock_out_at, paid_in_at: e.paid_in_at, paid_out_at: e.paid_out_at, breaks: (e.breaks ?? []) as never }).paid_minutes);
  // round(gross) - round(gross - unpaid) is never negative, so the combined break is >= 0.
  const unpaid = Math.max(0, gross - paid);
  const real = (e.breaks ?? []).filter(b => !b.paid && b.ended_at).sort((a, b) => Date.parse(a.started_at) - Date.parse(b.started_at));
  const brk = placeBreak(start, end, unpaid, real.length ? Date.parse(real[0].started_at) : null);
  return {
    user_id: e.user_id, display_name: e.display_name ?? '', payroll_id: cleanId(e.payroll_id), source: 'timesheet', ref_id: e.id,
    date: localDate(start, tz), start_at: at(start), end_at: at(end), break_start_at: brk ? at(brk[0]) : null, break_end_at: brk ? at(brk[1]) : null,
    paid_minutes: gross - unpaid, unpaid_break_minutes: unpaid,
    unpaid_breaks: real.map(b => ({ start_at: at(roundMinute(Date.parse(b.started_at))), minutes: Math.round((Date.parse(b.ended_at!) - Date.parse(b.started_at)) / MIN) })),
    location_id: e.location_id, location_code: cleanId(e.location_code), location_name: e.location_name, position_id: e.position_id, position_name: e.position_name,
  };
}

/** Lines that can go in a file (the person has a payroll ID) and the people left out because they have none. */
export function splitByPayrollId(lines: PayLine[]): { included: PayLine[]; missing: { user_id: UUID; display_name: string; lines: number }[] } {
  const included: PayLine[] = [];
  const missing = new Map<string, { user_id: UUID; display_name: string; lines: number }>();
  for (const l of lines) {
    if (l.payroll_id) { included.push(l); continue; }
    const m = missing.get(l.user_id) ?? { user_id: l.user_id, display_name: l.display_name, lines: 0 };
    m.lines++; missing.set(l.user_id, m);
  }
  const byName = <T extends { display_name: string }>(a: T, b: T) => a.display_name.localeCompare(b.display_name, 'en-GB');
  return { included: included.sort((a, b) => byName(a, b) || a.start_at.localeCompare(b.start_at)), missing: [...missing.values()].sort(byName) };
}

// ---- formatters ----------------------------------------------------------------------------------------------------

export type PayrollFileFormat = 'qbap_times' | 'qbap_units';
export const PAYROLL_FILE_FORMATS: { id: PayrollFileFormat; label: string; hint: string }[] = [
  { id: 'qbap_times', label: 'Start and end times', hint: 'Date, start, end and one break per line. Paid hours are exact to the minute.' },
  { id: 'qbap_units', label: 'Hours (units)', hint: 'Date and paid hours to 2 decimal places, no times.' },
];
export const isPayrollFileFormat = (v: unknown): v is PayrollFileFormat => PAYROLL_FILE_FORMATS.some(f => f.id === v);

export interface PayrollFileOptions {
  timezone: string;
  /** Work type external IDs: by position id, else the default (blank = the employee's default in payroll). */
  work_type_default?: string | null;
  work_types?: Record<string, string> | null;
  /** t606u: building id -> the payroll product's location ID (this format's product only). */
  location_ids?: Record<string, string> | null;
}

export interface PayrollFormatter {
  id: string;
  columns: string[];
  row: (l: PayLine, o: PayrollFileOptions) => (string | number)[];
}

export const workTypeFor = (l: Pick<PayLine, 'position_id'>, o: Pick<PayrollFileOptions, 'work_type_default' | 'work_types'>) =>
  ((l.position_id && o.work_types?.[l.position_id]) || o.work_type_default || '').trim();

// ---- building -> payroll location mapping (t606u) ------------------------------------------------------------------

/**
 * Payroll products a file format belongs to. Location mappings are stored per product (settings.payroll_location_ids
 * = { product: { building id: location ID } }), so each product's file formats share one mapping and another product
 * (Xero, Sage, BrightPay) gets its own section later.
 */
export type PayrollProduct = 'qbap';
export const PAYROLL_PRODUCTS: { id: PayrollProduct; label: string; location_label: string; formats: PayrollFileFormat[] }[] = [
  { id: 'qbap', label: 'QuickBooks Advanced Payroll', location_label: 'QuickBooks location', formats: ['qbap_times', 'qbap_units'] },
];
export const isPayrollProduct = (v: unknown): v is PayrollProduct => PAYROLL_PRODUCTS.some(p => p.id === v);
export const payrollProductOf = (format: PayrollFileFormat): PayrollProduct => PAYROLL_PRODUCTS.find(p => p.formats.includes(format))?.id ?? 'qbap';
/** product -> building id -> location ID. */
export type PayrollLocationIds = Partial<Record<PayrollProduct, Record<string, string>>>;

/** A line's location ID: the building's mapping for this product, else the building code, else blank (payroll uses the employee's default location). */
export const locationIdFor = (l: Pick<PayLine, 'location_id' | 'location_code'>, o: Pick<PayrollFileOptions, 'location_ids'>) =>
  (o.location_ids?.[l.location_id] ?? '').trim() || (l.location_code ?? '').trim();

/** Buildings with lines in the file and no location mapping, by name. */
export function unmappedLocations(lines: Pick<PayLine, 'location_id' | 'location_code' | 'location_name'>[], ids: Record<string, string> | null | undefined) {
  const out = new Map<string, { location_id: UUID; name: string; code: string | null; lines: number }>();
  for (const l of lines) {
    if ((ids?.[l.location_id] ?? '').trim()) continue;
    const x = out.get(l.location_id) ?? { location_id: l.location_id, name: l.location_name, code: (l.location_code ?? '').trim() || null, lines: 0 };
    x.lines++;
    out.set(l.location_id, x);
  }
  return [...out.values()].sort((a, b) => a.name.localeCompare(b.name, 'en-GB'));
}

const localDate = (t: number, tz: string) => DateTime.fromMillis(t, { zone: tz }).toISODate()!;
const local = (isoAt: string, tz: string) => DateTime.fromISO(isoAt, { zone: 'utc' }).setZone(tz);
const hhmm = (isoAt: string, tz: string) => local(isoAt, tz).toFormat('HH:mm');
const gbDate = (d: ISODate) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
const hoursText = (m: number) => `${Math.floor(m / 60)}h${m % 60 ? ` ${String(m % 60).padStart(2, '0')}m` : ''}`;

/** Minutes between two local wall-clock times (what a payroll import computes from Date + Start + End). */
function wallMinutes(a: string, b: string, tz: string) {
  const x = local(a, tz), y = local(b, tz);
  const days = Math.round(DateTime.fromISO(y.toISODate()!, { zone: 'utc' }).diff(DateTime.fromISO(x.toISODate()!, { zone: 'utc' }), 'days').days);
  return days * 1440 + (y.hour * 60 + y.minute) - (x.hour * 60 + x.minute);
}

/** The comment on a line: the Hub reference, position, the real breaks when they differ from the break columns, and anything unusual. */
export function payLineComment(l: PayLine, tz: string, withTimes = true): string {
  const parts = [`Hub ${l.source} ${l.ref_id.slice(0, 8)}`];
  if (l.position_name) parts.push(l.position_name);
  const exactBreak = l.unpaid_breaks.length === 1 && l.unpaid_breaks[0].start_at && l.break_start_at === l.unpaid_breaks[0].start_at && l.unpaid_breaks[0].minutes === l.unpaid_break_minutes;
  if (l.unpaid_break_minutes && (!withTimes || !exactBreak)) {
    const list = l.unpaid_breaks.map(b => `${b.start_at ? hhmm(b.start_at, tz) : 'any time'} ${b.minutes}m`).join(' / ');
    parts.push(`Unpaid breaks ${l.unpaid_break_minutes} min${list ? ` (${list})` : ''}`);
  }
  if (withTimes) {
    if (localDate(Date.parse(l.end_at), tz) !== l.date) parts.push('Ends next day');
    const gross = (Date.parse(l.end_at) - Date.parse(l.start_at)) / MIN;
    if (wallMinutes(l.start_at, l.end_at, tz) !== gross) parts.push(`Clock change: ${hoursText(l.paid_minutes)} paid`);
  }
  return parts.join('; ');
}

/** QuickBooks Advanced Payroll custom timesheet import (date + start/end time layout). */
const QBAP_TIMES: PayrollFormatter = {
  id: 'qbap_times',
  columns: ['Employee External ID', 'Date', 'Start Time', 'End Time', 'Break Start Time', 'Break End Time', 'Location External ID', 'Work Type External ID', 'Comments'],
  row: (l, o) => [l.payroll_id ?? '', gbDate(l.date), hhmm(l.start_at, o.timezone), hhmm(l.end_at, o.timezone),
    l.break_start_at ? hhmm(l.break_start_at, o.timezone) : '', l.break_end_at ? hhmm(l.break_end_at, o.timezone) : '',
    locationIdFor(l, o), workTypeFor(l, o), payLineComment(l, o.timezone, true)],
};
/** QuickBooks Advanced Payroll custom timesheet import (date + units layout). */
const QBAP_UNITS: PayrollFormatter = {
  id: 'qbap_units',
  columns: ['Employee External ID', 'Date', 'Units', 'Location External ID', 'Work Type External ID', 'Comments'],
  row: (l, o) => [l.payroll_id ?? '', gbDate(l.date), (l.paid_minutes / 60).toFixed(2), locationIdFor(l, o), workTypeFor(l, o), payLineComment(l, o.timezone, false)],
};
export const PAYROLL_FORMATTERS: Record<PayrollFileFormat, PayrollFormatter> = { qbap_times: QBAP_TIMES, qbap_units: QBAP_UNITS };

/**
 * One CSV cell (RFC 4180). IDs and times go out exactly as stored so they match the payroll product; only free text
 * (comments) that would start like a spreadsheet formula gets a leading apostrophe.
 */
export function payrollCsvCell(v: string | number, freeText = false): string {
  let s = String(v ?? '');
  if (freeText && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** The whole file: header row, one row per line, CRLF line ends, UTF-8 without a byte-order mark. */
export function renderPayrollFile(format: PayrollFileFormat, lines: PayLine[], o: PayrollFileOptions): string {
  const f = PAYROLL_FORMATTERS[format];
  const last = f.columns.length - 1;
  const out = [f.columns.map(c => payrollCsvCell(c)).join(',')];
  for (const l of lines) out.push(f.row(l, o).map((v, i) => payrollCsvCell(v, i === last)).join(','));
  return out.join('\r\n') + '\r\n';
}

export const payrollFileName = (format: PayrollFileFormat, freq: PayFrequency, p: PayPeriod, source: PayLineSource) =>
  `qbap-timesheets_${freq.replace('_', '-')}_${p.start}_${p.end}_${source === 'shift' ? 'shifts' : 'timesheets'}${format === 'qbap_units' ? '_hours' : ''}.csv`;

// ---- API shapes ----------------------------------------------------------------------------------------------------

export interface PayrollFileQuery {
  format: PayrollFileFormat;
  source: PayLineSource;
  pay_frequency: PayFrequency;
  from: ISODate;
  to: ISODate;
  location_id?: UUID;
  /** Timesheet hours only: also include closed entries that aren't approved yet (off by default). */
  include_unapproved?: boolean;
}

export interface PayrollFileCheck {
  period: PayPeriod;
  rows: number;
  people: number;
  paid_minutes: number;
  /** On the schedule with hours in the period but no payroll ID: left out of the file. */
  missing_payroll_id: { user_id: UUID; display_name: string; lines: number }[];
  /** Hours in the period from people with no pay frequency set: on no schedule, so in no file. */
  no_pay_frequency: { user_id: UUID; display_name: string }[];
  /** Timesheet hours: entries in the period not approved yet (open, submitted or queried). */
  unapproved: { id: UUID; user_id: UUID; display_name: string; date: ISODate; status: string; closed: boolean }[];
  /** Shift hours: draft (unpublished) shifts in the period, left out. */
  draft_shifts: { id: UUID; user_id: UUID; display_name: string; date: ISODate }[];
  /** Shift hours: published shifts with changes not published yet (the file uses the shift as it is now). */
  unpublished_changes: number;
  /** t606u: buildings in the file with no location ID set for this payroll product (they use the building code, else blank). Warning only. */
  unmapped_locations: { location_id: UUID; name: string; code: string | null; lines: number }[];
}
